import React from 'react';
import styled from 'styled-components';

interface NativeBannerProps {
  className?: string;
  requireGestureOnMobile?: boolean;
  useSandboxOnMobile?: boolean;
}

const BannerContainer = styled.div`
  margin: 20px auto;
  min-height: 280px;
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  /* Assurer que le contenu interne respecte les coins arrondis */
  & > div {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 8px;
  }
`;

export const NativeBanner = ({ className, requireGestureOnMobile = true, useSandboxOnMobile = true }: NativeBannerProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Helper: injecte la pub
    const inject = () => {
      // Nettoyer le contenu précédent
      container.innerHTML = '';

      // Définir les options et le script selon le device
      let key: string; let width: number; let height: number;
      if (isMobile) {
        key = '92987117a9ee0308f58d2fc9c3da9da5';
        width = 300;
        height = 250;
      } else {
        key = '2154f7f7df439957dfc24240cd37dd3c';
        width = 728;
        height = 90;
      }

      if (isMobile && useSandboxOnMobile) {
        // Exécuter dans une iframe sandboxée pour empêcher les redirections top-level
        const iframe = document.createElement('iframe');
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
        iframe.setAttribute('referrerpolicy', 'no-referrer');
        iframe.style.width = `${width}px`;
        iframe.style.height = `${height}px`;
        iframe.style.border = '0';
        container.appendChild(iframe);
        const doc = iframe.contentDocument || (iframe as any).document;
        if (doc) {
          const optionsScript = doc.createElement('script');
          optionsScript.type = 'text/javascript';
          optionsScript.innerHTML = `atOptions = { key: '${key}', format: 'iframe', height: ${height}, width: ${width}, params: {} };`;
          doc.body.appendChild(optionsScript);

          const script = doc.createElement('script');
          script.type = 'text/javascript';
          script.src = `https://traverseseven.com/${key}/invoke.js`;
          try { (script as any).referrerPolicy = 'no-referrer'; } catch {}
          try { (script as any).crossOrigin = 'anonymous'; } catch {}
          script.setAttribute('data-ad', 'traverseseven');
          doc.body.appendChild(script);
        }
      } else {
        // Injecter dans le DOM courant
        const optionsScript = document.createElement('script');
        optionsScript.type = 'text/javascript';
        optionsScript.innerHTML = `atOptions = { key: '${key}', format: 'iframe', height: ${height}, width: ${width}, params: {} };`;
        container.appendChild(optionsScript);

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://traverseseven.com/${key}/invoke.js`;
        try { (script as any).referrerPolicy = 'no-referrer'; } catch {}
        try { (script as any).crossOrigin = 'anonymous'; } catch {}
        script.setAttribute('data-ad', 'traverseseven');
        container.appendChild(script);
      }
    };

    const start = () => {
      try { inject(); } catch (e) { console.error('[NativeBanner] inject failed', e); }
    };

    // Sur mobile, attendre un geste utilisateur si requis
    if (isMobile && requireGestureOnMobile) {
      let onGesture: any = null;
      onGesture = () => {
        try { window.removeEventListener('touchstart', onGesture, true); } catch {}
        try { window.removeEventListener('click', onGesture, true); } catch {}
        start();
      };
      try { window.addEventListener('touchstart', onGesture, { capture: true, passive: true }); } catch {}
      try { window.addEventListener('click', onGesture, { capture: true, passive: true }); } catch {}
      return () => {
        try { window.removeEventListener('touchstart', onGesture, true); } catch {}
        try { window.removeEventListener('click', onGesture, true); } catch {}
      };
    } else {
      start();
    }
  }, [isMobile, requireGestureOnMobile, useSandboxOnMobile]);

  return (
    <BannerContainer className={className} style={{minHeight: isMobile ? 250 : 90, minWidth: isMobile ? 300 : 728, justifyContent: 'center', alignItems: 'center'}}>
      <div ref={containerRef} style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: isMobile ? 300 : 728, height: isMobile ? 250 : 90}} />
    </BannerContainer>
  );
};
